import { getListingHotBadge } from "@/lib/utils";

interface ListingHotBadgeProps {
  published_at: string | null | undefined;
  /**
   * "md"  → pill badge used on card image overlays (slightly larger)
   * "sm"  → compact badge used in table rows / title lines
   */
  size?: "md" | "sm";
}

/**
 * Renders a "🔥 This Hour" or "✦ New Today" badge based on the listing's
 * publish timestamp, or nothing if it was published over a day ago / not yet published.
 *
 * Rules (single source of truth — edit getListingHotBadge in lib/utils.ts):
 *   • This Hour  → published today AND within 4 hours of publish time
 *   • New Today  → published today (> 4 h ago)  OR  published yesterday
 *   • Nothing    → published 2+ days ago, or not yet published
 */
export default function ListingHotBadge({
  published_at,
  size = "md",
}: ListingHotBadgeProps) {
  const badge = getListingHotBadge(published_at);
  if (!badge) return null;

  const isThisHour = badge === "this-hour";

  const baseClass =
    "inline-flex items-center gap-1 rounded-full font-bold uppercase tracking-[0.12em] text-white shadow-sm shrink-0";

  const sizeClass =
    size === "sm"
      ? "px-1.5 py-0.5 text-[0.58rem]"
      : "px-2.5 py-0.5 text-[0.62rem]";

  const colorClass = isThisHour ? "bg-rose-600" : "bg-orange-500";

  return (
    <span className={`${baseClass} ${sizeClass} ${colorClass}`}>
      {isThisHour ? "🔥 This Hour" : "✦ New Today"}
    </span>
  );
}
